import { PREVIEW_PRODUCTS } from "./catalog";
import { benefitDetails } from "./landingBenefits";
import { subscribeNewsletter } from "./api";

// Bind the retained design to the existing React cart and account flows.
// React Strict Mode and route changes dispose every listener before rebinding.
export function initLanding(root, actions) {
  const controller = new AbortController();
  const query = selector => root.querySelector(selector);
  const listen = (element, event, handler) => element.addEventListener(event, handler, { signal: controller.signal });
  const menu = query("#menu");
  const navigation = query("#navigation");
  const productDialog = query("#product-dialog");
  const previousFocus = new WeakMap();
  let activeGallery = [];
  let activeIndex = 0;

  function setMenu(open) {
    navigation.classList.toggle("open", open);
    menu.setAttribute("aria-expanded", String(open));
    menu.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  }
  function openDialog(id) {
    setMenu(false);
    const dialog = query("#" + id);
    previousFocus.set(dialog, document.activeElement);
    dialog.showModal();
  }
  listen(menu, "click", () => setMenu(menu.getAttribute("aria-expanded") !== "true"));
  navigation.querySelectorAll("a").forEach(link => listen(link, "click", () => setMenu(false)));
  listen(document, "keydown", event => {
    if (event.key === "Escape" && menu.getAttribute("aria-expanded") === "true") {
      setMenu(false);
      menu.focus();
    }
  });
  root.querySelectorAll("[data-close]").forEach(button => listen(button, "click", () => query("#" + button.dataset.close).close()));
  root.querySelectorAll("dialog").forEach(dialog => {
    listen(dialog, "close", () => {
      const trigger = previousFocus.get(dialog);
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    });
    listen(dialog, "click", event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
  });

  function renderGallery() {
    const image = activeGallery[activeIndex];
    query("#detail-image").src = image.src;
    query("#detail-image").alt = image.alt;
    query("#gallery-count").textContent = (activeIndex + 1) + " of " + activeGallery.length;
    query("#gallery-caption").textContent = image.caption;
    query("#gallery-tip").textContent = image.tip || "Serving imagery is illustrative. Prepare using the final product label.";
    const dots = query("#gallery-dots");
    dots.replaceChildren();
    activeGallery.forEach((item, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "gallery-dot";
      button.setAttribute("aria-label", "Show image " + (index + 1) + ": " + item.caption);
      if (index === activeIndex) button.setAttribute("aria-current", "true");
      listen(button, "click", () => {
        activeIndex = index;
        renderGallery();
        query("#gallery-dots").children[index].focus();
      });
      dots.append(button);
    });
  }
  function moveGallery(step) {
    activeIndex = (activeIndex + step + activeGallery.length) % activeGallery.length;
    renderGallery();
  }
  listen(query("#gallery-prev"), "click", () => moveGallery(-1));
  listen(query("#gallery-next"), "click", () => moveGallery(1));
  listen(productDialog, "keydown", event => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      moveGallery(event.key === "ArrowLeft" ? -1 : 1);
    }
  });
  root.querySelectorAll("[data-product]").forEach(button => listen(button, "click", () => {
    const plant = button.dataset.product === "plant";
    const product = PREVIEW_PRODUCTS[plant ? 0 : 1];
    activeGallery = product.gallery;
    activeIndex = 0;
    renderGallery();
    query("#detail-name").textContent = product.name;
    query("#detail-label").textContent = plant ? "PLANT PROTEIN · COCOA" : "EGG WHITE PROTEIN · COCOA · LAUNCHING SOON";
    query("#detail-description").textContent = plant
      ? "Two plant protein sources, brought together in a considered cocoa blend. Developed with digestive comfort at the heart of the formulation brief."
      : "A different protein source, with the same purposeful ingredient philosophy. Our second LENIS blend is launching soon.";
    query("#detail-allergen").textContent = plant
      ? "Plant-based formulation. Final allergen and cross-contact information must be checked on the finished product label."
      : "Contains egg. Not suitable for vegans or people with an egg allergy. Check the final label for complete allergen information.";
    const facts = query("#detail-facts");
    facts.replaceChildren();
    for (const [value, label] of [[product.protein_per_serving, "protein target"], [product.serving_size + " g", "serving"], [product.servings, "servings"]]) {
      const span = document.createElement("span");
      const strong = document.createElement("b");
      strong.textContent = value;
      span.append(strong, document.createTextNode(label));
      facts.append(span);
    }
    query("#detail-add").hidden = !plant;
    query("#detail-contact").hidden = plant;
    openDialog("product-dialog");
  }));
  listen(query("#add-plant"), "click", actions.addPlant);
  listen(query("#detail-add"), "click", () => { productDialog.close(); actions.addPlant(); });
  listen(query("[data-cart]"), "click", actions.openCart);
  listen(query("[data-account]"), "click", actions.openAccount);
  root.querySelectorAll("[data-panel]").forEach(button => listen(button, "click", () => openDialog(button.dataset.panel)));
  root.querySelectorAll("[data-benefit]").forEach(button => listen(button, "click", () => {
    const benefit = benefitDetails[button.dataset.benefit];
    query("#benefit-detail-label").textContent = benefit.label;
    query("#benefit-title").textContent = benefit.title;
    query("#benefit-detail-text").textContent = benefit.text;
    query("#benefit-detail-note").textContent = benefit.note;
    query("#benefit-detail-image").src = benefit.image;
    query("#benefit-detail-image").alt = benefit.alt;
    query("#benefit-dialog").classList.toggle("botanical-detail", !!benefit.botanical);
    const source = query("#benefit-source");
    source.hidden = !benefit.source && !benefit.botanical;
    source.href = benefit.botanical
      ? "https://commons.wikimedia.org/wiki/File:Acacia_senegal_-_K%C3%B6hler%E2%80%93s_Medizinal-Pflanzen-004.jpg"
      : "https://www.gov.uk/government/publications/great-britain-nutrition-and-health-claims-nhc-register";
    source.textContent = benefit.botanical ? "Botanical illustration: F. E. Köhler · Public domain ↗" : "See the claims reference ↗";
    openDialog("benefit-dialog");
  }));
  const pause = query("#announcement-pause");
  listen(pause, "click", () => {
    const paused = pause.getAttribute("aria-pressed") !== "true";
    pause.setAttribute("aria-pressed", String(paused));
    pause.setAttribute("aria-label", paused ? "Resume announcements" : "Pause announcements");
    pause.textContent = paused ? "▶" : "Ⅱ";
    query(".announcement").classList.toggle("paused", paused);
  });
  listen(query("#welcome-signup"), "submit", async event => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    const button = form.querySelector('button[type="submit"]');
    if (button.disabled) return;
    button.disabled = true;
    query("#signup-status").textContent = "Joining LENIS…";
    try {
      await subscribeNewsletter(form.elements.email.value.trim());
      if (!controller.signal.aborted) {
        form.reset();
        query("#signup-status").textContent = "You’re on the LENIS list. Thank you for joining.";
      }
    } catch {
      if (!controller.signal.aborted) query("#signup-status").textContent = "We couldn’t complete your signup. Please try again or contact help@lenisnutrition.co.uk.";
    } finally {
      if (!controller.signal.aborted) button.disabled = false;
    }
  });
  return () => {
    controller.abort();
    root.querySelectorAll("dialog[open]").forEach(dialog => dialog.close());
  };
}


